import type { Card } from '@mtg-deck-tool/shared';
import { getCardManaCost, getCardTypeLine, getCardArtist } from '@mtg-deck-tool/shared';
import { useDeckStore } from '../../stores/deck-store.js';
import { ManaCostIcons } from '../shared/index.js';
import { useSymbolMap } from '../../hooks/useSymbolMap.js';
import { useCardPreview } from '../../contexts/CardPreviewContext.js';

interface SideboardCardRowProps {
  card: Card;
}

export function SideboardCardRow({ card }: SideboardCardRowProps) {
  const symbolMap = useSymbolMap();
  const moveToDeck = useDeckStore((s) => s.moveToDeck);
  const removeFromSideboard = useDeckStore((s) => s.removeFromSideboard);
  const openCardDetail = useDeckStore((s) => s.openCardDetail);
  const { setHoveredCard } = useCardPreview();

  const manaCost = getCardManaCost(card);
  const typeLine = getCardTypeLine(card);
  const artist = getCardArtist(card);

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 border-b border-gray-800/60 hover:bg-gray-800/40 transition-colors group"
      onMouseEnter={() => setHoveredCard(card)}
      onMouseLeave={() => setHoveredCard(null)}
    >
      {/* Card info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => { setHoveredCard(card); openCardDetail(card, false); }}
            className="text-xs font-medium text-gray-200 truncate hover:text-orange-300 hover:underline cursor-pointer bg-transparent border-none p-0 text-left"
          >
            {card.name}
          </button>
          {manaCost && (
            <ManaCostIcons manaCost={manaCost} symbolMap={symbolMap} size={11} className="shrink-0" />
          )}
        </div>
        <span className="text-[10px] text-gray-600 leading-tight block truncate">{typeLine}</span>
        {artist && (
          <span className="text-[10px] text-gray-700 leading-tight block truncate">Art by {artist}</span>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => moveToDeck(card.oracleId)}
          className="px-2 py-0.5 text-[10px] font-medium bg-orange-600/20 hover:bg-orange-600 text-orange-400 hover:text-white rounded transition-colors border border-orange-700/40 hover:border-orange-600"
        >
          Move to Deck
        </button>
        <button
          onClick={() => removeFromSideboard(card.oracleId)}
          className="px-2 py-0.5 text-[10px] font-medium bg-gray-700/50 hover:bg-red-900/50 text-gray-500 hover:text-red-300 rounded transition-colors"
        >
          Remove
        </button>
      </div>
    </div>
  );
}
